// Phase 1A Step 34 — Landing hero section.
// Verbatim port from handoff/landing-reference/app.jsx Hero component.
// Headline + serif strapline + CTAs + 3-stat strip on the left;
// the live Calculator on the right.

import { Link } from "react-router-dom";
import { Calculator } from "./Calculator.tsx";

interface HeroProps {
  stake: number;
  setStake: (v: number) => void;
  weeks: number;
  setWeeks: (v: number) => void;
  odds: number;
  takeout: number;
  symbol: string;
  headline: string;
  mode: "protected" | "full";
  setMode: (m: "protected" | "full") => void;
}

// Doctrinal trust strip. NEVER win-rate / CLV / active-user counts
// before the Daily Intelligence Ledger has 6+ months of sealed data
// (per STRIKZA_Daily_Intelligence_Ledger.docx + DualSwarm doctrine).
// Show architecture facts instead — these are verifiable from source.
const HERO_STATS: ReadonlyArray<{ v: string; l: string }> = [
  { v: "18", l: "AI agents · 2 swarms" },
  { v: "Sealed", l: "Daily ledger · 00:00 GMT" },
  { v: "Global", l: "Every fixture · every league" },
];

export function Hero(props: HeroProps): JSX.Element {
  const { stake, setStake, weeks, setWeeks, odds, takeout, symbol, headline, mode, setMode } =
    props;
  const lines = headline.split(/,\s*/);
  return (
    <section
      className="sz-l-hero"
      style={{
        position: "relative",
        minHeight: "var(--vh-full)",
        paddingTop: 120,
        paddingBottom: 100,
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <div className="kente-bg" />
      <div
        style={{
          position: "absolute",
          top: 100,
          left: 0,
          right: 0,
          height: 1,
          background: "linear-gradient(90deg, transparent, var(--border), transparent)",
        }}
      />

      <div className="container" style={{ position: "relative", zIndex: 2 }}>
        <div
          className="sz-l-hero-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.05fr",
            gap: 60,
            alignItems: "center",
          }}
        >
          {/* Left — headline */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginBottom: 32,
              }}
            >
              <span className="live-dot" />
              <span className="eyebrow">Dual-swarm engine · Built for Ghana &amp; Nigeria</span>
            </div>
            <h1
              className="display"
              style={{
                fontSize: "clamp(72px, 9vw, 148px)",
                color: "var(--text)",
                letterSpacing: "0.005em",
                lineHeight: 0.88,
                marginBottom: 32,
              }}
            >
              {lines.map((ln, i) => (
                <div key={i} style={{ whiteSpace: "nowrap" }}>
                  {ln.replace(/\.$/, "")}
                  {i === lines.length - 1 && <span style={{ color: "var(--accent)" }}>.</span>}
                </div>
              ))}
            </h1>
            <p
              className="serif"
              style={{
                fontSize: 22,
                fontStyle: "italic",
                lineHeight: 1.42,
                color: "var(--text-muted)",
                maxWidth: 520,
                marginBottom: 40,
                fontWeight: 400,
              }}
            >
              A swarm of AI agents argues every slip until consensus emerges. You approve.{" "}
              <span style={{ color: "var(--text)" }}>
                Half off the table every week, half rides on.
              </span>{" "}
              Discipline, automated.
            </p>
            <div
              style={{
                display: "flex",
                gap: 14,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Link to="/auth" className="btn btn-primary">
                Start Free <span className="btn-arrow">→</span>
              </Link>
              <a href="#agents" className="btn btn-ghost">
                Watch the engine
              </a>
            </div>

            {/*
              GAP-09: Verified Return Certificate trust badge per
              STRIKZA_OddsJam_Gaps_v1.0.docx. Mounted on Hero (and
              again on Pricing per the same spec). Doctrinal posture:
              the certificate is the trust mechanism — surface it
              early, no win-rate boast.
            */}
            <a
              href="#ledger"
              style={{
                marginTop: 24,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 16px",
                border: "1px solid var(--accent-deep)",
                background: "rgba(212, 168, 67, 0.06)",
                color: "var(--text)",
                textDecoration: "none",
                fontSize: 12,
                lineHeight: 1.3,
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 9,
                  letterSpacing: "0.18em",
                  color: "var(--accent)",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                Verified Return · in audit
              </span>
              <span style={{ color: "var(--text-muted)" }}>
                Third-party 90-day CLV+ record published within 4 months of launch
              </span>
              <span
                className="mono"
                style={{
                  fontSize: 11,
                  color: "var(--accent)",
                  letterSpacing: "0.1em",
                }}
              >
                →
              </span>
            </a>

            <div
              className="sz-l-hero-stats"
              style={{
                marginTop: 56,
                paddingTop: 24,
                borderTop: "1px solid var(--border)",
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 24,
              }}
            >
              {HERO_STATS.map((s, i) => (
                <div key={i}>
                  <div
                    className="display"
                    style={{ fontSize: 32, color: "var(--accent)", lineHeight: 1 }}
                  >
                    {s.v}
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: "var(--text-muted)",
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      marginTop: 6,
                    }}
                  >
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — calculator */}
          <div>
            <Calculator
              stake={stake}
              setStake={setStake}
              weeks={weeks}
              setWeeks={setWeeks}
              odds={odds}
              takeout={takeout}
              symbol={symbol}
              mode={mode}
              setMode={setMode}
            />
          </div>
        </div>
      </div>

      <div
        className="sz-l-scroll-cue"
        style={{
          position: "absolute",
          bottom: 32,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontFamily: "var(--mono)",
          fontSize: 10,
          letterSpacing: "0.18em",
          color: "var(--text-dim)",
          textTransform: "uppercase",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 1,
            height: 32,
            background: "linear-gradient(180deg, transparent, var(--accent-dim), transparent)",
          }}
        />
        Scroll to read the math
      </div>
    </section>
  );
}
